// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utility functions for estimating close run processing time.
 *
 * The close run process can handle 2,400-3,600 assets per second.
 * There is also a fixed overhead of approximately 1 second to start
 * and 1 second to close the transaction, regardless of asset count.
 */
import { formatDuration } from '@/utilities/format-duration';

// Processing speed constants (assets per second)
const MIN_ASSETS_PER_SECOND = 2400;
const MAX_ASSETS_PER_SECOND = 3600;

// Fixed overhead time in seconds (1 second to start + 1 second to close)
const FIXED_OVERHEAD_SECONDS = 2;

/**
 * Represents an estimated time range with minimum and maximum values.
 */
interface EstimatedTimeRange {
  minSeconds: number;
  maxSeconds: number;
}

/**
 * Calculates the estimated time range for creating a close run based on asset count.
 *
 * The calculation uses:
 * - Minimum time: assets / MAX_ASSETS_PER_SECOND + FIXED_OVERHEAD
 * - Maximum time: assets / MIN_ASSETS_PER_SECOND + FIXED_OVERHEAD
 *
 * @param assetCount - The number of assets in the organization
 * @returns An object containing the minimum and maximum estimated time in seconds
 */
export function calculateCloseRunTimeRange(
  assetCount: number
): EstimatedTimeRange {
  if (assetCount <= 0) {
    return {
      minSeconds: FIXED_OVERHEAD_SECONDS,
      maxSeconds: FIXED_OVERHEAD_SECONDS,
    };
  }

  const minSeconds = Math.ceil(
    assetCount / MAX_ASSETS_PER_SECOND + FIXED_OVERHEAD_SECONDS
  );
  const maxSeconds = Math.ceil(
    assetCount / MIN_ASSETS_PER_SECOND + FIXED_OVERHEAD_SECONDS
  );

  return { minSeconds, maxSeconds };
}

/**
 * Formats an estimated time range to a human-readable string.
 *
 * @param range - The estimated time range
 * @returns A formatted string like "2-5 seconds" or "27-42 seconds"
 */
export function formatTimeRange(range: EstimatedTimeRange): string {
  if (range.minSeconds === range.maxSeconds) {
    return formatDuration(range.minSeconds);
  }

  // For short durations (under 60 seconds), show as "X-Y seconds"
  if (range.maxSeconds < 60) {
    return `${String(range.minSeconds)}-${String(range.maxSeconds)} seconds`;
  }

  // For longer durations, format each part separately
  return `${formatDuration(range.minSeconds)} to ${formatDuration(range.maxSeconds)}`;
}
